import { useState, useEffect } from 'react'
import { X, Image } from 'lucide-react'
import { useCreateProduct, useUpdateProduct } from '@/hooks/use-shop'
import MediaPicker from '@/components/media/media-picker'
import type { Tables } from '@/types/supabase'

type ShopProduct = Tables<'shop_products'>

interface FormState {
  name: string
  category: string
  price_idr: string
  description: string
  external_url: string
  is_available: boolean
}

const EMPTY_FORM: FormState = {
  name: '',
  category: '',
  price_idr: '',
  description: '',
  external_url: '',
  is_available: true,
}

function formatPrice(value: string): string {
  // strip non-digits
  return value.replace(/\D/g, '')
}

interface ProductFormModalProps {
  open: boolean
  onClose: () => void
  product?: ShopProduct
}

export default function ProductFormModal({ open, onClose, product }: ProductFormModalProps) {
  const isEdit = !!product
  const createProduct = useCreateProduct()
  const updateProduct = useUpdateProduct()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [coverAssetId, setCoverAssetId] = useState<string | null>(null)
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)

  useEffect(() => {
    if (open) {
      if (product) {
        setForm({
          name: product.name,
          category: product.category ?? '',
          price_idr: product.price_idr != null ? String(product.price_idr) : '',
          description: product.description ?? '',
          external_url: product.external_url ?? '',
          is_available: product.is_available,
        })
        setCoverUrl(product.cover_url ?? null)
        setCoverAssetId((product as unknown as { asset_id?: string | null }).asset_id ?? null)
      } else {
        setForm(EMPTY_FORM)
        setCoverUrl(null)
        setCoverAssetId(null)
      }
      createProduct.reset()
      updateProduct.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id])

  const isPending = createProduct.isPending || updateProduct.isPending
  const mutationError = createProduct.error ?? updateProduct.error

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const priceRaw = form.price_idr.replace(/\D/g, '')
    const payload = {
      name: form.name.trim(),
      category: form.category.trim() || null,
      price_idr: priceRaw ? parseInt(priceRaw, 10) : null,
      description: form.description.trim() || null,
      cover_url: coverUrl,
      asset_id: coverAssetId,
      external_url: form.external_url.trim() || null,
      is_available: form.is_available,
    }

    if (isEdit && product) {
      updateProduct.mutate({ id: product.id, ...payload }, { onSuccess: onClose })
    } else {
      createProduct.mutate(payload, { onSuccess: onClose })
    }
  }

  if (!open) return null

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold">{isEdit ? 'Edit Produk' : 'Tambah Produk'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Cover image */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Foto Produk</p>
            {coverUrl ? (
              <div className="relative w-full h-36 rounded-lg overflow-hidden bg-muted">
                <img src={coverUrl} alt="preview" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => { setCoverUrl(null); setCoverAssetId(null) }}
                  className="absolute top-2 right-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMediaPickerOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border h-24 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Image className="h-4 w-4" />
                Pilih dari Media
              </button>
            )}
            {coverUrl && (
              <button
                type="button"
                onClick={() => setMediaPickerOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
              >
                <Image className="h-3.5 w-3.5" />
                Ganti Foto
              </button>
            )}
          </div>

          {/* Name */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Nama Produk *</label>
            <input
              required
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Nama produk"
            />
          </div>

          {/* Category + Price */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Kategori</label>
              <input
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                placeholder="Apparel, Photobook, dll"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Harga (Rp)</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.price_idr}
                onChange={(e) => set('price_idr', formatPrice(e.target.value))}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                placeholder="150000"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Deskripsi</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-none"
              placeholder="Deskripsi produk (opsional)"
            />
          </div>

          {/* External URL */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Link Toko Eksternal</label>
            <input
              type="url"
              value={form.external_url}
              onChange={(e) => set('external_url', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="https://tokopedia.com/... (opsional)"
            />
            <p className="text-xs text-muted-foreground">Link ke Tokopedia, Shopee, Tiktok Shop, dll</p>
          </div>

          {/* Available toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_available}
              onChange={(e) => set('is_available', e.target.checked)}
              className="rounded border-border accent-primary"
            />
            <span className="text-sm">Produk tersedia untuk dibeli</span>
          </label>

          {/* Error */}
          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan. Coba lagi.'}
            </p>
          )}

          {/* Footer actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah Produk'}
            </button>
          </div>
        </form>
      </div>
    </div>

    <MediaPicker
      open={mediaPickerOpen}
      onClose={() => setMediaPickerOpen(false)}
      onSelect={(assetId, url) => {
        setCoverAssetId(assetId)
        setCoverUrl(url)
        setMediaPickerOpen(false)
      }}
    />
    </>
  )
}
